import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Battery, Zap, Settings, ShieldCheck, Cpu, Globe, Loader2, FileText, ChevronRight } from 'lucide-react';

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const productSlug = searchParams.get('p');
  const productDetailRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/product');
        if (!res.ok) {
          throw new Error('Kunne ikke hente produkter fra databasen.');
        }
        const data = await res.json();
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Sync selected product with URL search param
  useEffect(() => {
    if (products.length > 0) {
      if (productSlug) {
        const found = products.find(
          p => p.slug?.da === productSlug || p.slug === productSlug || p.id === productSlug
        );
        if (found) {
          setSelectedProduct(found);
          // Smooth scroll to the showcase section or product detail
          setTimeout(() => {
            if (window.innerWidth < 1024) {
              productDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
              const element = document.getElementById('produktsortiment');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }
          }, 100);
        } else {
          setSelectedProduct(products[0]);
        }
      } else {
        setSelectedProduct(products[0]);
      }
    }
  }, [productSlug, products]);

  // Helper to get series name
  const getSeriesName = (series) => {
    if (!series) return 'Andre';
    return series.trim();
  };

  // Group products by series
  const groupedProducts = products.reduce((acc, prod) => {
    const series = getSeriesName(prod.product_series);
    if (!acc[series]) {
      acc[series] = [];
    }
    acc[series].push(prod);
    return acc;
  }, {});

  // Sort series names: STAR T first, STAR H second, others alphabetically
  const sortedSeries = Object.keys(groupedProducts).sort((a, b) => {
    const order = { 'STAR T': 1, 'STAR H': 2 };
    const orderA = order[a] || 999;
    const orderB = order[b] || 999;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });

  return (
    <main className="pt-24 overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 bg-slate-50 text-slate-900 overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter leading-none mb-8">
              Avancerede Energiløsninger
            </h1>
            <p className="text-xl md:text-2xl text-slate-600 font-medium leading-relaxed mb-10">
              GMA leverer næste generations batteriteknologi til industri og erhverv. 
              Fra skalerbare ESS-systemer til skræddersyede OEM-løsninger.
            </p>
          </motion.div>
        </div>
      </section>

      {/* C&I ESS Section */}
      <section id="ci-ess" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <div className="lg:w-7/12">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-1 bg-slate-900"></div>
                  <span className="text-slate-900 font-black uppercase tracking-[0.2em] text-sm">Erhverv & Industri</span>
                </div>
                <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-slate-900 mb-8">
                  C&I Energy Storage Systems
                </h2>
                <div className="prose prose-lg text-slate-600 max-w-none">
                  <p className="mb-6">
                    Vores Commercial & Industrial (C&I) energilagringssystemer er designet til at optimere energiforbruget for virksomheder og industrianlæg. Ved at integrere avanceret litium-batteriteknologi muliggør vi betydelige besparelser og øget driftssikkerhed.
                  </p>
                  <p className="mb-8 font-semibold text-slate-800">
                    Hvorfor vælge GMA C&I ESS?
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { icon: <Zap className="text-slate-900" />, title: "Peak Shaving", desc: "Reducer de dyre belastningstoppe i dit energiforbrug." },
                    { icon: <Battery className="text-slate-900" />, title: "Egetforbrug", desc: "Maksimer udnyttelsen af egen sol- eller vindenergi." },
                    { icon: <ShieldCheck className="text-slate-900" />, title: "Backup Power", desc: "Sikr kritisk infrastruktur mod strømafbrydelser." },
                    { icon: <Globe className="text-slate-900" />, title: "Grid Services", desc: "Deltag i frekvensregulering og støt elnettet." }
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4 p-4 bg-slate-50 border-l-4 border-slate-900">
                      <div className="mt-1">{item.icon}</div>
                      <div>
                        <h4 className="font-bold text-slate-900 uppercase text-sm mb-1">{item.title}</h4>
                        <p className="text-xs text-slate-600 leading-tight">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
            <div className="lg:w-5/12">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <img 
                  src="https://assets.auxodev.dk/assets/f4225015-9d61-46d9-9e4c-356d5fd3c807/a279b898-19b3-4818-93fc-5233000b0857.jpg" 
                  alt="Industrial ESS Unit" 
                  className="w-full h-auto shadow-2xl"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Produktsortiment Showcase Section */}
      <section id="produktsortiment" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-1 bg-[#62b2c9]"></div>
              <span className="text-[#62b2c9] font-black uppercase tracking-[0.2em] text-sm">Produktsortiment</span>
              <div className="w-8 h-1 bg-[#62b2c9]"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-slate-900 mb-6">
              Vores Batteriløsninger
            </h2>
            <p className="text-lg text-slate-600 font-medium">
              Udforsk vores avancerede energilagringssystemer. Vælg et produkt nedenfor for at se detaljerede tekniske specifikationer og datablade.
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#62b2c9] animate-spin mb-4" />
              <p className="text-slate-500 font-bold uppercase tracking-wider text-sm">Henter produkter...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-800 p-6 text-center max-w-xl mx-auto">
              <p className="font-bold mb-2">Der opstod en fejl ved indlæsning af produkter</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white border border-slate-200 p-12 text-center max-w-xl mx-auto">
              <p className="text-slate-500 font-bold uppercase tracking-wider">Ingen produkter tilgængelige i øjeblikket.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Product Tabs / List */}
              <div className="lg:col-span-4 space-y-6">
                <div className="text-xs font-black text-slate-400 uppercase tracking-widest px-2">
                  Vælg produkt ({products.length})
                </div>
                
                <div className="space-y-6">
                  {sortedSeries.map((seriesName) => {
                    const seriesProducts = groupedProducts[seriesName];
                    return (
                      <div key={seriesName} className="space-y-2">
                        <div className="text-[11px] font-black text-[#62b2c9] uppercase tracking-widest px-2 pb-1 border-b border-slate-200 mb-2">
                          {seriesName}
                        </div>
                        <div className="space-y-2">
                          {seriesProducts.map((prod) => {
                            const isSelected = selectedProduct?.id === prod.id;
                            const titleText = prod.title?.da || prod.title || 'Uden navn';
                            // Strip HTML tags from description for a clean subtitle
                            const cleanDesc = prod.description
                              ? prod.description.replace(/<[^>]*>/g, '').trim()
                              : 'ESS Container Løsning';
                            
                            return (
                              <button
                                key={prod.id}
                                onClick={() => {
                                  setSelectedProduct(prod);
                                  setSearchParams({ p: prod.slug?.da || prod.slug || prod.id });
                                  if (window.innerWidth < 1024) {
                                    setTimeout(() => {
                                      productDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                  }
                                }}
                                className={`w-full text-left p-4 transition-all flex items-center justify-between border-l-4 ${
                                  isSelected
                                    ? 'bg-slate-900 text-white border-[#62b2c9] shadow-md'
                                    : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-100 hover:border-slate-300'
                                }`}
                              >
                                <div className="pr-2 flex-1 min-w-0">
                                  <h3 className="font-black uppercase tracking-tight text-sm mb-0.5 truncate">
                                    {titleText}
                                  </h3>
                                  <p className={`text-[10px] ${isSelected ? 'text-slate-400' : 'text-slate-500'} truncate`}>
                                    {cleanDesc}
                                  </p>
                                </div>
                                <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${isSelected ? 'text-[#62b2c9] translate-x-1' : 'text-slate-400'}`} />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-white p-6 border border-slate-200 mt-8">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="text-[#62b2c9] w-6 h-6" />
                    <h4 className="font-black uppercase tracking-tight text-slate-900 text-sm">Brug for rådgivning?</h4>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-4">
                    Vores ingeniører sidder klar til at hjælpe dig med at dimensionere det helt rigtige batterianlæg til din virksomhed.
                  </p>
                  <a
                    href="/kontakt"
                    className="inline-block text-xs font-black uppercase tracking-wider text-[#62b2c9] hover:text-[#62b2c9]/80 transition-colors"
                  >
                    Kontakt rådgiver &rarr;
                  </a>
                </div>
              </div>

              {/* Product Detail Showcase */}
              <div ref={productDetailRef} className="lg:col-span-8 scroll-mt-24">
                <AnimatePresence mode="wait">
                  {selectedProduct && (
                    <motion.div
                      key={selectedProduct.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -15 }}
                      transition={{ duration: 0.4 }}
                      className="flex flex-col gap-8 bg-white border border-slate-200 p-6 md:p-8 shadow-sm"
                    >
                      {/* Product Image & Description */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="relative bg-slate-100 overflow-hidden aspect-[4/3] flex items-center justify-center border border-slate-200">
                          {selectedProduct.billede?.[0] ? (
                            <img
                              src={selectedProduct.billede[0]}
                              alt={selectedProduct.title?.da || selectedProduct.title}
                              className="w-full h-full object-contain p-4 transition-transform duration-500 hover:scale-105"
                            />
                          ) : (
                            <div className="text-slate-400 text-sm font-bold uppercase">Ingen illustration</div>
                          )}
                          {selectedProduct.product_series && (
                            <div className="absolute top-3 left-3 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1">
                              {selectedProduct.product_series}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col justify-between space-y-6">
                          <div>
                            <h3 className="text-2xl font-black uppercase tracking-tight text-slate-900 mb-3">
                              {selectedProduct.title?.da || selectedProduct.title}
                            </h3>
                            <div 
                              className="text-slate-600 text-sm leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0"
                              dangerouslySetInnerHTML={{ __html: selectedProduct.description }}
                            />
                          </div>

                          <div className="pt-4 border-t border-slate-100">
                            <a
                              href="/kontakt"
                              className="w-full text-center block bg-[#62b2c9] hover:bg-[#62b2c9]/90 text-white py-4 font-black uppercase tracking-widest text-xs transition-all"
                            >
                              Få et skræddersyet tilbud
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Technical Specifications Sheet */}
                      <div className="bg-slate-950 text-slate-100 p-6 border-l-4 border-[#62b2c9] flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-6">
                            <Cpu className="text-[#62b2c9] w-5 h-5" />
                            <h4 className="font-black uppercase tracking-wider text-xs text-slate-400">
                              Tekniske Specifikationer
                            </h4>
                          </div>

                          {(() => {
                            const parseSpecifications = (htmlString) => {
                              if (!htmlString) return [];
                              const parser = new DOMParser();
                              const doc = parser.parseFromString(htmlString, 'text/html');
                              const paragraphs = Array.from(doc.querySelectorAll('p'));
                              
                              const items = [];
                              paragraphs.forEach((p) => {
                                const text = p.textContent.trim();
                                if (!text) return;
                                
                                const isHeader = p.querySelector('strong') || p.querySelector('b') || (text.toLowerCase().includes('specifications') && !text.includes(':'));
                                
                                if (isHeader) {
                                  items.push({ type: 'header', value: text });
                                } else if (text.includes(':')) {
                                  const index = text.indexOf(':');
                                  const key = text.substring(0, index).trim();
                                  const val = text.substring(index + 1).trim();
                                  items.push({ type: 'pair', key, value: val });
                                } else {
                                  items.push({ type: 'text', value: text });
                                }
                              });
                              return items;
                            };

                            const specItems = parseSpecifications(selectedProduct.specifications);

                            return (
                              <div className="overflow-y-auto max-h-[450px] pr-2 scrollbar-thin scrollbar-thumb-slate-800">
                                <table className="w-full text-left border-collapse">
                                  <tbody>
                                    {specItems.map((item, idx) => {
                                      if (item.type === 'header') {
                                        return (
                                          <tr key={idx} className="border-b border-slate-800/60">
                                            <td colSpan={3} className="pt-6 pb-2 text-[#62b2c9] font-black uppercase tracking-wider text-[11px] first:pt-0">
                                              {item.value}
                                            </td>
                                          </tr>
                                        );
                                      }
                                      if (item.type === 'pair') {
                                        const hasPipe = item.value && item.value.includes('|');
                                        if (hasPipe) {
                                          const [val1, val2] = item.value.split('|').map(v => v.trim());
                                          return (
                                            <tr key={idx} className="border-b border-slate-900/60 hover:bg-slate-900/20 transition-colors">
                                              <td className="py-2.5 pr-4 font-bold text-slate-400 text-[10px] uppercase tracking-wider w-5/12 align-top">
                                                {item.key}
                                              </td>
                                              <td className="py-2.5 text-slate-200 text-xs font-medium align-top w-3.5/12 pr-2">
                                                {val1}
                                              </td>
                                              <td className="py-2.5 text-slate-200 text-xs font-medium align-top w-3.5/12">
                                                {val2}
                                              </td>
                                            </tr>
                                          );
                                        }
                                        return (
                                          <tr key={idx} className="border-b border-slate-900/60 hover:bg-slate-900/20 transition-colors">
                                            <td className="py-2.5 pr-4 font-bold text-slate-400 text-[10px] uppercase tracking-wider w-5/12 align-top">
                                              {item.key}
                                            </td>
                                            <td colSpan={2} className="py-2.5 text-slate-200 text-xs font-medium align-top">
                                              {item.value}
                                            </td>
                                          </tr>
                                        );
                                      }
                                      return (
                                        <tr key={idx} className="border-b border-slate-900/60">
                                          <td colSpan={3} className="py-2.5 text-slate-300 text-xs font-medium">
                                            {item.value}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="mt-8 pt-4 border-t border-slate-800 text-[10px] text-slate-500 font-bold uppercase tracking-wider flex justify-between">
                          <span>Standard: CE / IEC / UL</span>
                          <span>GMA Energy</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ODM/OEM Section */}
      <section id="odm-oem" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-1 bg-slate-900"></div>
              <span className="text-slate-900 font-black uppercase tracking-[0.2em] text-sm">Produktion & Design</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-slate-900 mb-8">
              ODM & OEM <span className="text-slate-900">Partnerskaber</span>
            </h2>
            <div className="prose prose-lg text-slate-600 max-w-4xl">
              <p className="mb-6">
                GMA er ikke kun en leverandør; vi er din tekniske partner. Vi tilbyder omfattende ODM (Original Design Manufacturer) og OEM (Original Equipment Manufacturer) tjenester til virksomheder, der ønsker deres egne brandede energiløsninger.
              </p>
              <p className="mb-8">
                Vores team af ingeniører arbejder tæt sammen med dig fra konceptudvikling og hardware-design til software-integration og certificering. Vi sikrer, at dit produkt lever op til de strengeste globale standarder.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
              {[
                { icon: <Settings size={20} />, text: "Skræddersyet kabinet- og systemdesign" },
                { icon: <Cpu size={20} />, text: "Proprietær BMS integration og optimering" },
                { icon: <Globe size={20} />, text: "Hjælp til lokal certificering (CE, IEC, UN38.3)" },
                { icon: <ShieldCheck size={20} />, text: "White-label produktion med fuld QA" }
              ].map((item, index) => (
                <div key={index} className="flex flex-col gap-4 text-slate-800 font-bold uppercase tracking-tight text-sm p-6 bg-white shadow-sm border border-slate-100">
                  <div className="p-2 w-fit text-slate-900 bg-slate-50">{item.icon}</div>
                  {item.text}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Grid / CTA */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-12">
            Klar til at fremtidssikre din energi?
          </h2>
          <div className="flex flex-col md:flex-row justify-center gap-6">
            <a href="/#contact" className="bg-[#62b2c9] hover:bg-[#62b2c9]/90 text-white px-12 py-5 font-black uppercase tracking-widest transition-all">
              Kontakt os for tilbud
            </a>
            <a href="/om-os" className="border-2 border-white hover:bg-white hover:text-slate-900 text-white px-12 py-5 font-black uppercase tracking-widest transition-all">
              Læs om virksomheden
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProductsPage;
